"use client"

import { useEffect, useState } from "react"
import Sidebar from "./Sidebar"
import Header from "./Header"
import axios from "axios"
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaBuilding,
  FaShieldAlt,
  FaUsers,
  FaFolder,
  FaCreditCard,
  FaHashtag,
} from "react-icons/fa"
import { FiEdit } from "react-icons/fi";
import { RiLockPasswordLine } from "react-icons/ri"; // Tambahkan icon untuk reset password
import Notification from "./Notification"
import MFAManagement from "./MFAManagement"

const UserInformation = () => {
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true))
  const [userInfo, setUserInfo] = useState(null)
  const [userGroups, setUserGroups] = useState([])
  const [userProjects, setUserProjects] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [editData, setEditData] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState("")
  const [notification, setNotification] = useState(null)
  const [resetPwMode, setResetPwMode] = useState(false)
  const [resetPwStep, setResetPwStep] = useState(1) // 1: request, 2: confirm
  const [resetPwLoading, setResetPwLoading] = useState(false)
  const [resetPwError, setResetPwError] = useState("")
  const [resetPwCode, setResetPwCode] = useState("")
  const [resetPwNew, setResetPwNew] = useState("")
  const [resetPwOtp, setResetPwOtp] = useState(["", "", "", "", "", ""]) // Untuk OTP 6 digit
  const user = JSON.parse(localStorage.getItem("user"))
  const userId = user ? user.id_user : ""

  useEffect(() => {
    // Set background untuk seluruh halaman
    document.body.style.backgroundColor = "#f9fafb"
    document.documentElement.style.backgroundColor = "#f9fafb"

    // Cleanup saat komponen unmount
    return () => {
      document.body.style.backgroundColor = ""
      document.documentElement.style.backgroundColor = ""
    }
  }, [])

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true)
      try {
        const [infoRes, groupRes, projectRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/${userId}`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/user/${userId}/groups`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects/user/${userId}/projects`),
        ])
        setUserInfo(infoRes.data)
        setUserGroups(groupRes.data)
        setUserProjects(projectRes.data)
      } catch (error) {
        console.error("Error fetching user data:", error)
      } finally {
        setIsLoading(false)
      }
    }
    if (userId) fetchAll()
  }, [userId])

  const toggleSidebar = () => setSidebarOpen((prev) => !prev)

  const getInitials = (name) => {
    return (
      name
        ?.split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "U"
    )
  }

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "bg-red-100 text-red-800 border-red-200"
      case "manager":
        return "bg-blue-100 text-blue-800 border-blue-200"
      case "user":
        return "bg-gray-100 text-gray-800 border-gray-200"
      default:
        return "bg-gray-100 text-gray-600 border-gray-200"
    }
  }

  const LoadingSkeleton = () => (
    <div className="animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
    </div>
  )

  const handleEditClick = () => {
    setEditData({
      full_name: userInfo.full_name || "",
      username: userInfo.username || "",
      password: "",
      company_id: userInfo.company_id || "",
      role: userInfo.role || "",
      email: userInfo.email || "",
      phone: userInfo.phone || "",
    })
    setEditError("")
    setEditMode(true)
  }

  const handleEditChange = (e) => {
    const { name, value } = e.target
    setEditData((prev) => ({ ...prev, [name]: value }))
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    setEditLoading(true)
    setEditError("")
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/${userId}`,
        editData
      )
      setEditMode(false)
      // Refresh user info
      const infoRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/${userId}`)
      setUserInfo(infoRes.data)
      setNotification({ type: "success", message: "Profile updated successfully!" })
    } catch (err) {
      setEditError(
        err.response?.data?.message ||
        "Failed to update user information."
      )
      setNotification({ type: "error", message: err.response?.data?.message || "Failed to update user information." })
    } finally {
      setEditLoading(false)
    }
  }

  const handleResetPwRequest = async () => {
    setResetPwLoading(true)
    setResetPwError("")
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/reset-password-request`,
        { email: user.email }
      )
      setResetPwStep(2)
    } catch (err) {
      setResetPwError(
        err.response?.data?.message || "Failed to send reset code."
      )
    } finally {
      setResetPwLoading(false)
    }
  }

  const handleResetPwConfirm = async (e) => {
    e.preventDefault()
    setResetPwLoading(true)
    setResetPwError("")
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/reset-password-confirm`,
        {
          email: user.email,
          verification_code: resetPwCode,
          new_password: resetPwNew,
        }
      )
      setResetPwMode(false)
      setNotification({ type: "success", message: "Password reset successfully!" })
      setResetPwStep(1)
      setResetPwCode("")
      setResetPwNew("")
    } catch (err) {
      setResetPwError(
        err.response?.data?.message || "Failed to reset password."
      )
    } finally {
      setResetPwLoading(false)
    }
  }

  // Tambahkan efek untuk reset OTP saat modal dibuka/tutup
  useEffect(() => {
    if (resetPwMode) {
      setResetPwOtp(["", "", "", "", "", ""])
      setResetPwCode("")
    }
  }, [resetPwMode])

  // Handler untuk OTP input
  const handleOtpChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 1)
    let newOtp = [...resetPwOtp]
    newOtp[idx] = val
    setResetPwOtp(newOtp)
    // Fokus ke kotak berikutnya jika ada input
    if (val && idx < 5) {
      document.getElementById(`otp-input-${idx + 1}`)?.focus()
    }
    // Update resetPwCode
    setResetPwCode(newOtp.join(""))
  }

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !resetPwOtp[idx] && idx > 0) {
      document.getElementById(`otp-input-${idx - 1}`)?.focus()
    }
  }

  // Tambahkan handler paste OTP di bawah handler OTP lain
  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6)
    if (paste.length === 6) {
      setResetPwOtp(paste.split(""))
      setResetPwCode(paste)
      // Fokus ke input terakhir
      setTimeout(() => {
        document.getElementById(`otp-input-5`)?.focus()
      }, 0)
      e.preventDefault()
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
      <div className="flex flex-1 min-h-screen min-w-0 max-w-full overflow-x-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
          <div className="p-4 lg:p-8 pt-20 lg:pt-24">
            <div className="max-w-6xl mx-auto">
              {/* Header Section */}
              <div className="mb-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">User Information</h1>
                    <p className="text-gray-600">Manage your account details and view your access permissions</p>
                  </div>

                  {userInfo && (
                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                        {getInitials(userInfo.full_name)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{userInfo.full_name}</p>
                        <p className="text-sm text-gray-500 truncate">{userInfo.email}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {isLoading ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
                  <div className="flex items-center gap-6 mb-8">
                    <div className="w-16 h-16 bg-gray-200 rounded-full animate-pulse"></div>
                    <div>
                      <div className="h-6 bg-gray-200 rounded w-48 mb-2 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse"></div>
                  </div>
                </div>
              ) : userInfo ? (
                <div className="space-y-6">
                  {/* Profile Information */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FaUser className="text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
                          <p className="text-sm text-gray-600">Your personal account details</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 p-2 rounded-full border border-blue-200 bg-blue-50 transition-colors"
                          onClick={handleEditClick}
                          title="Edit Profile"
                        >
                          {/* <FiEdit className="w-4 h-4" /> */}
                          <span className="font-semibold text-xs px-2">Edit Profile</span>
                        </button>
                        <button
                          className="flex items-center gap-2 text-orange-600 hover:text-orange-800 p-2 rounded-full border border-orange-200 bg-orange-50 transition-colors"
                          onClick={() => { setResetPwMode(true); setResetPwStep(1); setResetPwError(""); }}
                          title="Reset Password"
                        >
                          {/* <RiLockPasswordLine className="w-4 h-4" /> */}
                          <span className="font-semibold text-xs px-2">Reset Password</span>
                        </button>
                      </div>
                    </div>

                    {/* Edit Modal */}
                    {editMode && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
                          <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-900">Edit Profile</h3>
                            <button
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              onClick={() => setEditMode(false)}
                            >
                              <span className="text-2xl leading-none">&times;</span>
                            </button>
                          </div>
                          <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
                            {editError && (
                              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                                {editError}
                              </div>
                            )}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                              <input
                                type="text"
                                name="full_name"
                                value={editData.full_name}
                                onChange={handleEditChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                              <input
                                type="text"
                                name="username"
                                value={editData.username}
                                onChange={handleEditChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            </div>
                            {/* Hapus kolom Password */}
                            {/* Hapus kolom Company ID */}
                            {/* Hapus kolom Role */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                              <input
                                type="email"
                                name="email"
                                value={editData.email}
                                onChange={handleEditChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                              <input
                                type="text"
                                name="phone"
                                value={editData.phone}
                                onChange={handleEditChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                              <button
                                type="button"
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                onClick={() => setEditMode(false)}
                                disabled={editLoading}
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                disabled={editLoading}
                              >
                                {editLoading ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                            <FaUser className="w-4 h-4" />
                            Full Name
                          </div>
                          <p className="text-gray-900 font-medium">{userInfo.full_name}</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                            <FaUser className="w-4 h-4" />
                            Username
                          </div>
                          <p className="text-gray-900 font-medium">{userInfo.username}</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                            <FaEnvelope className="w-4 h-4" />
                            Email
                          </div>
                          <p className="text-gray-900 font-medium break-all">{userInfo.email}</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                            <FaPhone className="w-4 h-4" />
                            Phone
                          </div>
                          <p className="text-gray-900 font-medium">{userInfo.phone || "Not provided"}</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                            <FaShieldAlt className="w-4 h-4" />
                            Role
                          </div>
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRoleBadgeColor(userInfo.role)}`}
                          >
                            {userInfo.role}
                          </span>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                            <FaBuilding className="w-4 h-4" />
                            Company
                          </div>
                          <p className="text-gray-900 font-medium">{userInfo.company_name}</p>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                              <FaHashtag className="w-4 h-4" />
                              Company ID
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <code className="text-sm text-gray-800">{userInfo.company_id}</code>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                              <FaCreditCard className="w-4 h-4" />
                              Billing Account ID
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <code className="text-sm text-gray-800">{userInfo.billing_account_id}</code>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Groups */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <FaUsers className="text-green-600" />
                            </div>
                            <div>
                              <h2 className="text-xl font-semibold text-gray-900">Groups</h2>
                              <p className="text-sm text-gray-600">Your group memberships</p>
                            </div>
                          </div>
                          <span className="bg-white border border-gray-200 text-gray-600 px-3 py-1 rounded-full text-sm font-medium">
                            {userGroups.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {userGroups.length === 0 ? (
                          <div className="text-center py-8">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <FaUsers className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500">You are not a member of any group</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {userGroups.map((group) => (
                              <div
                                key={group.group_id}
                                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                              >
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                  <FaUsers className="w-4 h-4 text-green-600" />
                                </div>
                                <span className="font-medium text-gray-900">{group.group_name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Projects */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <FaFolder className="text-purple-600" />
                            </div>
                            <div>
                              <h2 className="text-xl font-semibold text-gray-900">Projects</h2>
                              <p className="text-sm text-gray-600">Your project access</p>
                            </div>
                          </div>
                          <span className="bg-white border border-gray-200 text-gray-600 px-3 py-1 rounded-full text-sm font-medium">
                            {userProjects.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {userProjects.length === 0 ? (
                          <div className="text-center py-8">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <FaFolder className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500">You don't have access to any project</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {userProjects.map((project, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                              >
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                  <FaFolder className="w-4 h-4 text-purple-600" />
                                </div>
                                <span className="font-medium text-gray-900">{project}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* MFA Security Section */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <FaShieldAlt className="text-amber-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900">Security & Authentication</h2>
                          <p className="text-sm text-gray-600">Manage your multi-factor authentication settings</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <MFAManagement />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaUser className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load user information</h3>
                  <p className="text-gray-500">
                    Please try refreshing the page or contact support if the problem persists.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Reset Password Modal */}
      {resetPwMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                Reset Password
              </h3>
              <button
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => { setResetPwMode(false); setResetPwStep(1); setResetPwError(""); }}
              >
                <span className="text-2xl leading-none">&times;</span>
              </button>
            </div>
            <div className="p-6">
              {resetPwStep === 1 ? (
                <>
                  {resetPwError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                      {resetPwError}
                    </div>
                  )}
                  <p className="mb-4 text-gray-700">
                    Click the button below to send a verification code to your email: <span className="font-semibold">{user.email}</span>
                  </p>
                  <button
                    className="w-full flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    onClick={handleResetPwRequest}
                    disabled={resetPwLoading}
                  >
                    {/* <RiLockPasswordLine className="w-5 h-5" /> */}
                    {resetPwLoading ? "Sending..." : "Send Verification Code"}
                  </button>
                </>
              ) : (
                <form onSubmit={handleResetPwConfirm} className="space-y-4">
                  {resetPwError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                      {resetPwError}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Verification Code
                    </label>
                    <div className="flex gap-2 justify-start">
                      {[0,1,2,3,4,5].map((idx) => (
                        <input
                          key={idx}
                          id={`otp-input-${idx}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className="w-10 h-12 text-center text-xl border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={resetPwOtp[idx]}
                          onChange={e => handleOtpChange(e, idx)}
                          onKeyDown={e => handleOtpKeyDown(e, idx)}
                          onPaste={e => handleOtpPaste(e)}
                          autoFocus={idx === 0}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={resetPwNew}
                      onChange={e => setResetPwNew(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      onClick={() => { setResetPwMode(false); setResetPwStep(1); setResetPwError(""); }}
                      disabled={resetPwLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      disabled={resetPwLoading}
                    >
                      {resetPwLoading ? "Saving..." : "Reset Password"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserInformation
